// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// devextreme imports
import { DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// component imports
import { WorkOrderComponent } from '../../../work-orders-module/components/work-order/work-order.component';

// service imports
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowEditorService } from '../../_services/workflow-editor.service';
import { WorkflowService } from '../../_services/workflow.service';

// model imports
import { WorkOrderRawDto } from '../../../work-orders-module/_models/work-order-raw-dto';
import { WorkFlowManageWorkOrderParametersDto } from '../../_models/common/manage-work-order-parameters-dto';
import { BaseWorkFlowWorkOrderParametersDto } from '../../_models/common/base-wf-wo-parameter-dto';
import { WorkFlowWorkOrderResultDto } from '../../_models/common/wf-work-order-result-dto';

// constant imports
import { MessageInterval } from '../../../_constants/messages-intervals';

@Component({
    selector: 'app-workflow-workorder-selector',
    templateUrl: './workflow-workorder-selector.component.html',
    styleUrls: ['./workflow-workorder-selector.component.scss']
})
export class WorkflowWorkorderSelectorComponent implements OnInit {
    @ViewChild('workOrderSelector', { static: false }) workOrderSelector: DxPopupComponent;
    @ViewChild('workOrderMain', { static: false }) workOrderMain: WorkOrderComponent;

    @Input() communityId: number;
    @Input() vendorId: number;
    @Input() workOrders: Array<number>;
    @Input() workflowId: number;
    @Input() step: string;
    @Input() payee: string;
    @Input() vendorCode: string;
    @Input() showOnlyLinked = false;

    @Output() onClose = new EventEmitter<boolean>();
    @Output() onMarkWorkOrderComplete = new EventEmitter<Array<number>>();

    popupTitle: string;
    visible = false;
    loadingVisible = false;

    showWorkOrderEditor = false;
    showManageWorkAuthorization = false;
    showManageProposals = false;
    workOrderId: number;

    linkWorkOrderIds: Array<number> = [];
    unLinkWorkOrderIds: Array<number> = [];

    iconCheck = IconConstants.CHECK;
    iconFolder = IconConstants.FOLDER_PLUS;
    iconClose = IconConstants.CLOSE;
    iconAdd = IconConstants.ADD;
    iconRefresh = IconConstants.REFRESH;

    isSaved = false;

    constructor(
        private readonly workflowEditorService: WorkflowEditorService,
        private readonly workflowService: WorkflowService,
        private readonly errorService: ServerErrorService
    ) {}

    ngOnInit() {
        this.popupTitle = Utils.isFalse(this.showOnlyLinked)
            ? `Manage Work Orders For ${this.payee}`
            : 'Select open work orders currently to this invoice that you would like to automatically set as completed';
        this.linkWorkOrderIds = [];
        this.unLinkWorkOrderIds = [];
        if (Utils.isTrue(this.showOnlyLinked)) {
            this.getLinkedWorkOrders();
        } else {
            this.show();
        }
    }

    show(): void {
        this.visible = true;
    }

    onWorkOrderShown(): void {
        this.workOrderSelector.instance.repaint();
    }

    onWorkOrderClose(): void {
        this.closeWorkOrderSelector();
    }

    onWorkOrderIdsSelection(workOrderIds: Array<number>): void {
        this.linkWorkOrderIds = workOrderIds;
    }

    onWorkOrderIdsDeselection(workOrderIds: Array<number>): void {
        if (Utils.notNullOrEmpty(workOrderIds)) {
            workOrderIds.forEach((id) => {
                if (Utils.isInList(id, this.workOrders)) {
                    this.unLinkWorkOrderIds.push(id);
                }
            });
        }
    }

    onWorkOrderSave(): void {
        const baseParam: WorkFlowManageWorkOrderParametersDto = new WorkFlowManageWorkOrderParametersDto();
        baseParam.communityId = this.communityId;
        baseParam.workflowId = this.workflowId;
        if (Utils.notNullOrEmpty(this.linkWorkOrderIds)) {
            baseParam.workOrderIdsToLink = this.linkWorkOrderIds;
        }
        if (Utils.notNullOrEmpty(this.unLinkWorkOrderIds)) {
            baseParam.workOrderIdsToUnLink = this.unLinkWorkOrderIds;
        }
        this.loadingVisible = true;
        this.workflowService.manageWorkOrders(baseParam).subscribe(
            () => {
                notify(
                    'Successfully! saved changes to AP Invoice Work Order(s) links.',
                    'Success',
                    MessageInterval.successMsgInterval
                );
                this.isSaved = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    onMarkWorkOrderCompleted(): void {
        this.onMarkWorkOrderComplete.emit(this.linkWorkOrderIds);
        this.closeWorkOrderSelector();
    }

    onAddNewWorkOrder(): void {
        this.resetWorkOrder();
        this.showWorkOrderEditor = true;
    }

    onRefreshClick(): void {
        this.refreshWorkOrders();
    }

    onWorkOrderEditorShow(): void {
        this.showWorkOrderEditor = true;
    }

    onEditorClose(forceRefresh: boolean): void {
        this.showWorkOrderEditor = false;

        if (Utils.isTrue(forceRefresh)) {
            this.refreshWorkOrders();
        }
    }

    onManageProposalShow(workOrderInfo: WorkOrderRawDto): void {
        if (Utils.notNullAndDefined(workOrderInfo)) {
            this.communityId = workOrderInfo.communityId;
            this.workOrderId = workOrderInfo.workOrderId;
            this.isSaved = true;
        }

        this.showManageProposals = true;
    }

    onManageProposalClose(forceReload: boolean): void {
        if (Utils.isTrue(forceReload)) {
            this.refreshWorkOrders();
        }

        this.showManageProposals = false;
    }

    onManageWorkAuthorizationShow(workOrderInfo: WorkOrderRawDto): void {
        if (Utils.notNullAndDefined(workOrderInfo)) {
            this.communityId = workOrderInfo.communityId;
            this.workOrderId = workOrderInfo.workOrderId;
            this.isSaved = true;
        }

        this.showManageWorkAuthorization = true;
    }

    onManageWorkorderAuthorizationClose(): void {
        this.showManageWorkAuthorization = false;
    }

    isSaveDisabled(): boolean {
        return Utils.isNullOrEmpty(this.linkWorkOrderIds) && Utils.isNullOrEmpty(this.unLinkWorkOrderIds);
    }

    private getLinkedWorkOrders(): void {
        const baseParams: BaseWorkFlowWorkOrderParametersDto = new BaseWorkFlowWorkOrderParametersDto();
        baseParams.communityId = this.communityId;
        baseParams.workflowId = this.workflowId;
        if (Utils.notNullAndDefined(this.vendorId)) {
            baseParams.managementCompanyVendorId = this.vendorId;
        }

        this.workflowService.getOpenWorkOrders(baseParams).subscribe(
            (results: WorkFlowWorkOrderResultDto[]) => {
                if (Utils.notNullOrEmpty(results)) {
                    const openWorkOrders = results;
                    let openLinkedWorkOrders = results.filter((d) => Utils.isInList(d.workOrderId, this.workOrders));
                    if (Utils.notNullOrEmpty(openLinkedWorkOrders)) {
                        this.show();
                    } else {
                        openLinkedWorkOrders = openWorkOrders.filter((d) =>
                            Utils.isInList(this.workflowId, d.invoiceIds)
                        );
                        if (Utils.notNullOrEmpty(openLinkedWorkOrders)) {
                            this.show();
                        } else {
                            this.onMarkWorkOrderComplete.emit([]);
                        }
                    }
                } else {
                    this.onMarkWorkOrderComplete.emit([]);
                }
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private refreshWorkOrders(): void {
        this.isSaved = true;
        this.workOrderMain.refreshWorkOrders();
    }

    private resetWorkOrder(): void {
        this.workOrderId = null;
    }

    private closeWorkOrderSelector(): void {
        this.visible = false;
        this.onClose.emit(this.isSaved);
    }
}
